import { defineQuery } from "@rocicorp/zero";
import type { ExpressionBuilder } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql, type Schema } from "../schema";
import { FULL_MEMBER_ROLES } from "../constants";
import { channelMemberGuard, workspaceMemberGuard } from "./access-filters";

/**
 * Helper: returns a filter that allows access if the user is either:
 * 1. The user themselves (self), OR
 * 2. A full workspace member (sees all workspace users), OR
 * 3. A channel peer (collaborators only see users in shared channels)
 *
 * Branch 4 (historical message authors in sub's channels) was dropped
 * in PR 1b. It was the EXPENSIVE branch — `EXISTS over messages` ran at
 * ~560ms on zero-cache's SQLite. Soft-deleted memberships (PR 1a) now
 * keep kicked users resolvable via branches 2/3 because their
 * membership row stays present with `removed_at` set. Message renders
 * fall back to denormalized `sender_display_name` / `sender_avatar_url`
 * for the very rare case where all membership paths are gone.
 *
 * Asymmetric soft-delete semantics:
 *   - The TARGET user's membership CAN be soft-deleted (branches 2
 *     and 3 don't filter `removed_at` on the outer
 *     workspace_memberships / channel_memberships exists — kicked
 *     users stay visible for historical messages).
 *   - The CALLER (sub) must be an ACTIVE member (every inner
 *     `mq.where("user_id", sub)` filters `removed_at IS NULL` — see
 *     the channelMemberGuard import and branch 2's inline filter).
 *
 * To gate active-member operations, use the membership-active guards
 * in access-filters.ts. This filter is only for "can I resolve this
 * user record."
 */
function userAccessFilter(sub: string) {
  return (eb: ExpressionBuilder<"users", Schema>) =>
    eb.or(
      eb.cmp("id", sub),
      // Full workspace members see all workspace users (including former
      // members whose workspace_members row has removed_at set).
      eb.exists("workspace_memberships", (q) =>
        q.whereExists("workspace", (wq) =>
          wq.whereExists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("role", "IN", FULL_MEMBER_ROLES)
              .where("removed_at", "IS", null),
          ),
        ),
      ),
      // Channel peers (collaborators only see users in shared channels/DMs).
      // Note: the outer `channel_memberships` exists covers both active
      // and former memberships of the target user; the inner check only
      // requires sub's membership to be active.
      eb.exists("channel_memberships", (q) =>
        q.whereExists("channel", channelMemberGuard(sub)),
      ),
    );
}

export const usersQueries = {
  all: defineQuery(({ ctx: { sub } }) =>
    zql.users.where(userAccessFilter(sub)),
  ),
  byEmail: defineQuery(z.string(), ({ args: email, ctx: { sub } }) =>
    zql.users.where("email", email).where(userAccessFilter(sub)).one(),
  ),
  byId: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.users.where("id", id).where(userAccessFilter(sub)).one(),
  ),
  byChannel: defineQuery(z.string(), ({ args: channelId, ctx: { sub } }) =>
    zql.users.whereExists("channel_memberships", (q) =>
      q
        .where("channel_id", channelId)
        .whereExists("channel", channelMemberGuard(sub)),
    ),
  ),
  byWorkspace: defineQuery(z.string(), ({ args: workspaceId, ctx: { sub } }) =>
    zql.users
      .where(userAccessFilter(sub))
      .whereExists("workspace_memberships", (q) =>
        // Require the CALLER to be an active member of `workspaceId` too —
        // without the `workspace` guard, any user can enumerate which of
        // their resolvable peers belong to an arbitrary workspace. Matches
        // the membership gate the sibling workspace-scoped queries use.
        q
          .where("workspace_id", workspaceId)
          .whereExists("workspace", workspaceMemberGuard(sub)),
      ),
  ),
  byWorkspacesOfUser: defineQuery(({ ctx: { sub } }) =>
    zql.users.where(userAccessFilter(sub)),
  ),
  // Phase 1.5.6 — `users.typing`, `users.online` were retired here. They
  // referenced the hot columns dropped from the Zero schema in this PR.
  // Replacements live on `user_presence`:
  //   queries.user_presence.typing(channelId)
  //   queries.user_presence.online()
  // No callers remained at the time of removal (verified via PR audit).
  active: defineQuery(({ ctx: { sub } }) =>
    zql.users.where("is_deactivated", false).where(userAccessFilter(sub)),
  ),
  deactivated: defineQuery(({ ctx: { sub } }) =>
    zql.users.where("is_deactivated", true).where(userAccessFilter(sub)),
  ),
  // Always-on subscription that consolidates the four user-scoped
  // notification settings tables into a single Zero hub:
  //   - notification_preferences
  //   - channel_notification_overrides
  //   - dnd_settings
  //   - notification_keywords
  // Replaces the four standalone *.byUser() subscriptions opened by
  // useNotificationFilter. notification_events is intentionally NOT
  // folded in — events fire ~10× message-rate, which would re-emit
  // the whole composite on every notification and defeat structural
  // sharing. Saves 3 hubs per active user. Auth: outer `where("id",
  // sub)` gates everything; settings rows match transitively via
  // their user_id FK.
  notificationSettingsComposite: defineQuery(({ ctx: { sub } }) =>
    zql.users
      .where("id", sub)
      .related("notification_preferences")
      .related("channel_notification_overrides")
      .related("dnd_settings")
      .related("notification_keywords")
      .one(),
  ),
};
